import { I18n } from "@assets/strings";
import React, { useMemo, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Group } from "@domain/group/group";
import { useGroupAsset } from "@domain/group/hooks";
import { Media } from "@domain/media/media";
import { useIsMobile } from "@utils/hooks";
import { PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Secondary } from "@ui/components/containers/textBlocks";
import { Row, Stack, row } from "@ui/components/layout";
import { ZoomedAsset } from "@ui/components/media/zoomedAsset";
import { mobile } from "@ui/styles/utils";
import { ShareroomPdfThumbnil } from "./shareroomPdfTumbnil";
interface ShareroomPdfProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
}
export const ShareroomPdf: React.FC<ShareroomPdfProps> = ({ shareroom, ...htmlProps }) => {
	const { result: groupAsset, loading } = useGroupAsset(10, shareroom.id, "DOCUMENT");
	const [galleryContent, setGalleryContent] = useState<Media | null>(null);
	const isMobile = useIsMobile();
	const defaultCount = isMobile ? 2 : 3;
	const [showAllPictures, setShowAllPictures] = useState(groupAsset?.content_size ?? 0 < defaultCount);
	const displayedAssets = useMemo(
		() => (showAllPictures ? groupAsset?.content : groupAsset?.content.slice(0, defaultCount)),
		[showAllPictures, groupAsset?.content]
	);

	return (
		<Container {...htmlProps}>
			<SubtitleContainer>
				<StyledPrimarybold>
					{I18n.t("shareroom.pdf.title")} {`(${groupAsset?.content_size}).`}
				</StyledPrimarybold>
				<Row gap={8} justify="center" align="center">
					{groupAsset && groupAsset?.content_size > defaultCount && (
						<Fold>
							<span onClick={() => setShowAllPictures(!showAllPictures)}>
								{showAllPictures ? I18n.t("see_less") : I18n.t("see_more")}
							</span>
						</Fold>
					)}
				</Row>
			</SubtitleContainer>
			{galleryContent && <ZoomedAsset asset={galleryContent} onClose={() => setGalleryContent(null)} />}
			<Content>
				{loading ? (
					<ClipLoader />
				) : groupAsset?.content?.length === 0 ? (
					<Stack gap={10}>
						<Secondary>{I18n.t("shareroom.pdf.empty", { user: shareroom.name })}</Secondary>
					</Stack>
				) : (
					displayedAssets?.map((asset) => (
						<ShareroomPdfThumbnil
							key={asset.contentId}
							workspace={asset}
							onClick={() => setGalleryContent(asset.asset)}
						/>
					))
				)}
			</Content>
			{/* )} */}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 30px 40px;
`;

const SubtitleContainer = styled.div`
	${row(10, "space-between", "center")};
	margin-bottom: 20px;
`;

const Content = styled.div`
	max-width: 100%;
	margin: 0 auto;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-gap: 45px;

	@media ${mobile} {
		grid-template-columns: repeat(1, 1fr);
		grid-gap: 15px;
	}
`;
const Fold = styled(PrimaryMedium)`
	text-align: end;
	color: ${({ theme }) => theme.colors.secondary};
	> span {
		cursor: pointer;
	}
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;
